/**
 * NativeAppProvider
 * React Context Provider for native app functionality
 * Wraps the app and provides native services to all components
 */

import React, { createContext, useContext, useEffect, useState } from 'react';
import { useNativeApp } from '../hooks/useNativeApp';

// Create the context
const NativeAppContext = createContext(null);

/**
 * Hook to access native app context
 */
export function useNativeContext() {
  const context = useContext(NativeAppContext);
  if (!context) {
    throw new Error('useNativeContext must be used within NativeAppProvider');
  }
  return context;
}

/**
 * Provider component that wraps the app
 */
export function NativeAppProvider({ children }) {
  const nativeApp = useNativeApp();
  const [offlineMode, setOfflineMode] = useState(false);
  
  // Handle offline mode
  useEffect(() => {
    if (!nativeApp.initialized) return;
    
    setOfflineMode(!nativeApp.isOnline);
    
    // Show offline banner when network changes
    if (!nativeApp.isOnline && nativeApp.isNative) {
      // Could trigger a toast notification here
      console.log('Device is offline');
    }
  }, [nativeApp.isOnline, nativeApp.initialized, nativeApp.isNative]);
  
  // Handle app resume events
  useEffect(() => {
    const handleAppResume = () => {
      console.log('App resumed from background');
      // Refresh data or re-sync here if needed
    };
    
    window.addEventListener('appResumed', handleAppResume);
    return () => window.removeEventListener('appResumed', handleAppResume);
  }, []);
  
  const value = {
    ...nativeApp,
    offlineMode,
    
    // Helper methods
    showOfflineWarning: () => {
      if (!nativeApp.isOnline) {
        alert('You are currently offline. Some features may be unavailable.');
        return true;
      }
      return false;
    },
    
    // Check if a native feature is available
    hasFeature: (featureName) => {
      switch (featureName) {
        case 'camera':
          return nativeApp.isNative;
        case 'push':
          return nativeApp.pushEnabled;
        case 'location':
          return nativeApp.isNative;
        case 'storage':
          return true; // Always available
        default:
          return false;
      }
    }
  };
  
  return (
    <NativeAppContext.Provider value={value}>
      {/* Offline Banner */}
      {offlineMode && nativeApp.isNative && (
        <div className="fixed top-0 left-0 right-0 bg-amber-500 text-white text-center py-2 z-[9999] text-sm">
          You're offline. Changes will sync when connected.
        </div>
      )}
      
      {children}
    </NativeAppContext.Provider>
  );
}

export default NativeAppProvider;
